import type { Metro } from '../../types/metro';
import { ratioColor } from '../../lib/map-utils';
import { lensedRatioForMetro } from '../../lib/occupation-utils';
import { useFilterStore } from '../../stores/filterStore';

interface MetroTooltipProps {
  readonly metro: Metro;
}

/**
 * On-hover tooltip body for a metro bubble. 2026-05-18 feedback pass
 * stripped the previous credentials/jobs/shortage summary lines —
 * the hover state now shows ONLY the metro name + the credentials-
 * to-jobs ratio, with the eyebrow rephrased to remove the
 * "credentials shortage" label (the new visual encoding is a
 * continuous red→green scale, not a categorical shortage band).
 *
 * CR-06 (2026-07-08, GX5): the bubble underneath this tooltip already
 * lenses its color/size to the selected occupational-group filter
 * (`MetroBubble` -> `lensedRatioForMetro()`), but this tooltip kept
 * rendering `metro.ratio` (the overall figure) — so hovering a metro
 * under an OG filter showed a DIFFERENT number than the bubble's
 * color implied. Now reads the same filter store + shared helper so
 * the headline number always matches what's under the cursor. The
 * "in {group}" qualifier only renders when the ratio is genuinely
 * OG-scoped (`isLensed`) — when the metro doesn't carry the selected
 * group we fall back to the overall ratio and stay silent about the
 * group rather than mislabeling the number.
 *
 * Wrapped by Leaflet's `<Tooltip>` chrome in MetroBubble — this
 * component is the inner content only.
 */
export function MetroTooltip({ metro }: MetroTooltipProps) {
  const occupationalGroupId = useFilterStore((s) => s.occupationalGroupId);
  const lens = lensedRatioForMetro(metro, occupationalGroupId);
  return (
    <div className="min-w-[170px] font-sans">
      <div className="mb-[6px] text-[12px] font-bold text-text-1">{metro.name}</div>
      <div
        className="mb-[2px] text-[22px] font-extrabold leading-none"
        style={{ color: ratioColor(lens.ratio) }}
      >
        {lens.ratio.toFixed(2)}
      </div>
      <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-text-4">
        Credentials-to-jobs ratio
      </div>
      {lens.isLensed && (
        <div className="mt-0.5 text-[9px] font-semibold text-text-3">
          in {lens.lensSectorName}
        </div>
      )}
    </div>
  );
}
